import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { INFECTION_TEXT_LIMITS, ISOLATION_FLAG_STATUS_LABELS } from '@wodi/domain';
import type { IsolationFlagListView, IsolationFlagView } from '@wodi/shared';
import {
  Button,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  Section,
  Select,
  Tag,
  TextArea,
} from '@wodi/ui';
import { useState } from 'react';

import {
  endIsolationFlag,
  infectionKeys,
  infectionOptionsQuery,
  isolationFlagEnteredInError,
  isolationFlagsQuery,
  startIsolationFlag,
} from '../api/infection.ts';
import { patientKeys, patientVisits } from '../api/patients.ts';
import { useOnline } from '../hooks/use-online.ts';
import { dateTimeText, errorText, LoadFailed } from '../operations/format.tsx';
import styles from '../operations/operations.module.css';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';

// Isolation and precautions on the patient page (docs/plans/phase3-operations-modules.md
// §2.4). Passive: what is in place shows on the banner and the bed board and never blocks
// anything. Starting, ending and correcting a flag needs `infection.flag`.

const UNTIL_ENDED = 'patient';

function StartDialog({ patientId, onClose }: { patientId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const options = useQuery(infectionOptionsQuery);
  const visits = useQuery({
    queryKey: [...patientKeys.visits(patientId), 'open-for-flags'],
    queryFn: () => patientVisits(patientId, undefined),
  });
  const [precaution, setPrecaution] = useState<string | null>(null);
  const [tie, setTie] = useState<string>(UNTIL_ENDED);
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const start = useMutation({
    mutationFn: () =>
      startIsolationFlag(patientId, {
        precautionCode: precaution ?? '',
        visitId: tie === UNTIL_ENDED ? null : tie,
        note: note.trim() === '' ? null : note.trim(),
      }),
    onSuccess: (view) => {
      queryClient.setQueryData(infectionKeys.flags(patientId), view);
      void queryClient.invalidateQueries({ queryKey: patientKeys.banner(patientId) });
      toasts.show({ title: 'Precautions started' });
      onClose();
    },
  });
  const openVisits = (visits.data?.items ?? []).filter((v) => v.status === 'open');
  return (
    <Dialog
      title="Start precautions"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        {options.isError ? (
          <Notice tone="error">The precautions list couldn&apos;t be loaded. Try again.</Notice>
        ) : null}
        <Select
          label="Precaution"
          placeholder="Choose one"
          options={(options.data?.precautions ?? [])
            .filter((p) => p.active)
            .map((p) => ({ id: p.code, label: p.display }))}
          value={precaution}
          onChange={(key) => {
            setPrecaution(key === null ? null : String(key));
          }}
          {...(tried && precaution === null ? { errorMessage: 'Choose a precaution.' } : {})}
        />
        <Select
          label="How long"
          options={[
            { id: UNTIL_ENDED, label: 'Until someone ends it' },
            ...openVisits.map((v) => ({
              id: v.id,
              label: `While visit ${v.visitNumber} is open`,
            })),
          ]}
          value={tie}
          onChange={(key) => {
            setTie(key === null ? UNTIL_ENDED : String(key));
          }}
        />
        <TextArea
          label="Note (optional)"
          description="For example, what the precaution is for. Shown on this page only."
          value={note}
          onChange={setNote}
          maxLength={INFECTION_TEXT_LIMITS.note}
        />
        {start.isError ? (
          <Notice tone="error">{errorText(start.error, "It wasn't saved. Try again.")}</Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Go back
        </Button>
        <Button
          variant="primary"
          isPending={start.isPending}
          pendingLabel="Starting…"
          onPress={() => {
            setTried(true);
            if (precaution !== null) start.mutate();
          }}
        >
          Start precautions
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function FlagRow({
  flag,
  mayFlag,
  online,
  onClose,
}: {
  flag: IsolationFlagView;
  mayFlag: boolean;
  online: boolean;
  onClose: (flag: IsolationFlagView, outcome: 'end' | 'error') => void;
}) {
  return (
    <li className={styles.item}>
      <div className={styles.itemMain}>
        <span className={styles.tags}>
          <Tag state={flag.showing ? 'attention' : 'neutral'}>{flag.words}</Tag>
          {flag.status === 'active' && !flag.showing ? (
            <Tag state="neutral">Visit closed</Tag>
          ) : flag.status === 'active' ? null : (
            <Tag state="neutral">{ISOLATION_FLAG_STATUS_LABELS[flag.status]}</Tag>
          )}
        </span>
        <span className={styles.meta}>
          Started {dateTimeText(flag.startedAt)} by {flag.startedBy.displayName}
          {flag.visit === null ? ', until ended' : `, for visit ${flag.visit.visitNumber}`}
          {flag.statusChangedAt === null || flag.statusChangedBy === null
            ? ''
            : `. ${ISOLATION_FLAG_STATUS_LABELS[flag.status]} ${dateTimeText(flag.statusChangedAt)} by ${flag.statusChangedBy.displayName}: ${flag.statusReason ?? ''}`}
        </span>
        {flag.note === null ? null : <span className={styles.prose}>{flag.note}</span>}
      </div>
      {mayFlag && flag.status === 'active' ? (
        <span className={styles.actions}>
          <Button
            dense
            isDisabled={!online}
            onPress={() => {
              onClose(flag, 'end');
            }}
          >
            End precautions
          </Button>
          <Button
            dense
            variant="quiet"
            isDisabled={!online}
            onPress={() => {
              onClose(flag, 'error');
            }}
          >
            Entered in error
          </Button>
        </span>
      ) : null}
    </li>
  );
}

export function PatientPrecautionsSection({
  patientId,
  mayFlag,
}: {
  patientId: string;
  mayFlag: boolean;
}) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const flags = useQuery(isolationFlagsQuery(patientId));
  const [starting, setStarting] = useState(false);
  const [closing, setClosing] = useState<{
    flag: IsolationFlagView;
    outcome: 'end' | 'error';
  } | null>(null);
  const close = useMutation({
    mutationFn: (reason: string): Promise<IsolationFlagListView> =>
      closing?.outcome === 'error'
        ? isolationFlagEnteredInError(closing.flag.id, reason)
        : endIsolationFlag(closing?.flag.id ?? '', reason),
    onSuccess: (view) => {
      queryClient.setQueryData(infectionKeys.flags(patientId), view);
      void queryClient.invalidateQueries({ queryKey: patientKeys.banner(patientId) });
      toasts.show({
        title: closing?.outcome === 'error' ? 'Marked entered in error' : 'Precautions ended',
      });
      setClosing(null);
    },
  });
  return (
    <Section
      title="Isolation and precautions"
      description="Shown on the banner and the bed board while in place. They never block anything."
      {...(mayFlag
        ? {
            actions: (
              <Button
                isDisabled={!online}
                onPress={() => {
                  setStarting(true);
                }}
              >
                Start precautions
              </Button>
            ),
          }
        : {})}
    >
      {flags.isPending ? <LoadingBlock label="Loading precautions" rows={2} /> : null}
      {flags.isError ? (
        <LoadFailed
          error={flags.error}
          fallback="Precautions couldn't be loaded. Try again."
          onRetry={() => void flags.refetch()}
        />
      ) : null}
      {flags.isSuccess ? (
        flags.data.items.length === 0 ? (
          <p className={styles.meta}>No precautions recorded.</p>
        ) : (
          <ul className={styles.list} aria-label="Precautions recorded">
            {flags.data.items.map((flag) => (
              <FlagRow
                key={flag.id}
                flag={flag}
                mayFlag={mayFlag}
                online={online}
                onClose={(f, outcome) => {
                  close.reset();
                  setClosing({ flag: f, outcome });
                }}
              />
            ))}
          </ul>
        )
      ) : null}
      {starting ? (
        <StartDialog
          patientId={patientId}
          onClose={() => {
            setStarting(false);
          }}
        />
      ) : null}
      {closing === null ? null : (
        <ReasonDialog
          title={closing.outcome === 'error' ? 'Mark entered in error' : 'End precautions'}
          isOpen
          onOpenChange={(open) => {
            if (!open) setClosing(null);
          }}
          confirmLabel={closing.outcome === 'error' ? 'Mark entered in error' : 'End precautions'}
          pendingLabel="Saving…"
          isPending={close.isPending}
          error={close.isError ? errorText(close.error, 'That didn’t work. Try again.') : null}
          onConfirm={(reason) => {
            close.mutate(reason);
          }}
        />
      )}
    </Section>
  );
}
